import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Home() {
  const [thought, setThought] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/daily-thought").then(({ data }) => setThought(data)).catch(() => {});
  }, []);

  const go = (path) => () => navigate(user ? path : "/auth");

  return (
    <div className="min-h-screen bg-forge-900 text-forge-100">
      <Header />
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(115deg, rgba(15,14,13,0.2), rgba(15,14,13,0.72)), repeating-linear-gradient(90deg, rgba(217,115,37,0.1) 0 1px, transparent 1px 120px), repeating-linear-gradient(0deg, rgba(242,239,233,0.045) 0 1px, transparent 1px 72px), radial-gradient(circle at 18% 24%, rgba(217,115,37,0.24), transparent 32%), radial-gradient(circle at 82% 18%, rgba(181,143,68,0.16), transparent 30%), #0f0e0d",
          }}
          aria-hidden
        />
        <div className="absolute inset-0 bg-gradient-to-b from-forge-900/70 via-forge-900/85 to-forge-900" aria-hidden />
        <div className="absolute inset-0 grain" aria-hidden />

        <div className="relative max-w-5xl mx-auto px-6 lg:px-10 pt-24 sm:pt-32 pb-20 sm:pb-28">
          <div className="text-[10px] tracking-[0.4em] uppercase text-ember-500 mb-6 animate-fade-up">
            A forge for ideas
          </div>
          <h1
            className="font-serif font-light text-5xl sm:text-6xl lg:text-7xl text-forge-100 tracking-tight leading-[1.05] mb-8 text-balance animate-fade-up"
            data-testid="home-title"
          >
            ThoughtSmith™
          </h1>
          <p className="font-serif italic text-xl sm:text-2xl text-forge-100/80 mb-4 max-w-2xl animate-fade-up" style={{ animationDelay: "0.15s" }}>
            The Intellectual Mentor for Lifelong Learners.
          </p>
          <p className="font-sans text-base text-forge-100/60 max-w-xl tracking-wider mb-12 animate-fade-up" style={{ animationDelay: "0.3s" }}>
            Read.&nbsp;&nbsp;Think.&nbsp;&nbsp;Question.&nbsp;&nbsp;Grow.
          </p>
          <div className="flex flex-wrap items-center gap-4 animate-fade-up" style={{ animationDelay: "0.45s" }}>
            <button
              onClick={go("/learn")}
              className="px-7 py-3 bg-ember-500 hover:bg-ember-400 text-forge-900 font-semibold tracking-wide rounded-sm transition-all hover:-translate-y-0.5"
              data-testid="home-start-learning"
            >
              Start Learning
            </button>
            <button
              onClick={go("/progress")}
              className="px-7 py-3 border border-white/20 hover:border-ember-500 hover:text-ember-400 text-forge-100 tracking-wide rounded-sm transition-all hover:-translate-y-0.5"
              data-testid="home-my-progress"
            >
              My Progress
            </button>
          </div>
        </div>
      </section>

      {/* DAILY THOUGHT */}
      <section className="max-w-4xl mx-auto px-6 lg:px-10 py-20 sm:py-28">
        <div className="text-[10px] tracking-[0.4em] uppercase text-gold-500 mb-6">Daily Thought</div>
        <blockquote
          className="border-l-2 border-ember-500 pl-6 sm:pl-8 py-2"
          data-testid="daily-thought"
        >
          <p className="font-serif italic text-2xl sm:text-3xl lg:text-4xl text-forge-100 leading-snug text-balance">
            {thought ? `"${thought.text}"` : "Loading today's thought…"}
          </p>
          {thought && (
            <footer className="mt-6 text-sm uppercase tracking-[0.3em] text-forge-100/50">
              — {thought.author}
            </footer>
          )}
        </blockquote>
      </section>

      <div className="ember-divider max-w-3xl mx-auto" />

      {/* HOW IT WORKS */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 py-20">
        <div className="text-[10px] tracking-[0.4em] uppercase text-ember-500 mb-3">The Loop</div>
        <h2 className="font-serif text-4xl sm:text-5xl tracking-tight mb-12 max-w-2xl text-balance">
          A complete learning loop, not a tutor.
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/5">
          {[
            { n: "01", t: "Read", d: "Upload, browse, or generate a passage of intellectual depth." },
            { n: "02", t: "Decode", d: "Vocabulary Mentor surfaces challenging words with plain-language explanations." },
            { n: "03", t: "Question", d: "Intellectual Mentor probes comprehension, evidence, perspective, and reflection." },
            { n: "04", t: "Grow", d: "Track your articles, time, vocabulary, and reflections over time." },
          ].map((s) => (
            <div key={s.n} className="bg-forge-900 p-8 hover:bg-forge-800 transition-colors duration-300">
              <div className="text-xs font-mono text-ember-500 mb-4">{s.n}</div>
              <div className="font-serif text-2xl mb-3">{s.t}</div>
              <p className="text-sm text-forge-100/60 leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FUTURE */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 pb-12">
        <div className="text-[10px] tracking-[0.4em] uppercase text-ember-500 mb-3">Now Live</div>
        <h3 className="font-serif text-3xl sm:text-4xl tracking-tight mb-8 max-w-2xl">A Socratic sparring partner.</h3>
        <div className="grid md:grid-cols-2 gap-4 mb-16">
          <div className="p-6 border border-ember-500/30 bg-forge-800/40 rounded-sm">
            <div className="text-[10px] tracking-[0.3em] uppercase text-ember-500 mb-3">Debate Mentor</div>
            <p className="font-serif text-xl text-forge-100 mb-3">Never declares a winner. Always sharpens reasoning.</p>
            <p className="text-sm text-forge-100/60 leading-relaxed">After you reflect, ThoughtSmith™ poses a Socratic challenge, asks one follow-up, and offers a balanced synthesis with an Intellectual Reasoning Rubric — evaluating the structure of your thought, never your beliefs.</p>
          </div>
          <div className="p-6 border border-gold-500/30 bg-forge-800/40 rounded-sm">
            <div className="text-[10px] tracking-[0.3em] uppercase text-gold-500 mb-3">Research Mentor</div>
            <p className="font-serif text-xl text-forge-100 mb-3">Not a search engine. An intellectual literacy coach.</p>
            <p className="text-sm text-forge-100/60 leading-relaxed">Map a topic, surface competing perspectives, identify evidence gaps, and learn to evaluate every category of source — peer-reviewed to advocacy — without being told what to think.</p>
          </div>
          <div className="p-6 border border-ember-500/30 bg-forge-800/40 rounded-sm md:col-span-2">
            <div className="text-[10px] tracking-[0.3em] uppercase text-ember-500 mb-3">Root Word Explorer · Intellectual Journal</div>
            <p className="font-serif text-xl text-forge-100 mb-3">Learn the patterns. Keep the record.</p>
            <p className="text-sm text-forge-100/60 leading-relaxed">Each unfamiliar word surfaces its root, prefix, suffix, and origin — language as architecture. After every reading, write to your future self in the Intellectual Journal.</p>
          </div>
          <Link to={user ? "/writing" : "/auth"} className="p-6 border border-gold-500/30 bg-forge-800/40 rounded-sm md:col-span-2 hover:border-gold-500/60 transition-all">
            <div className="text-[10px] tracking-[0.3em] uppercase text-gold-500 mb-3">Writing Mentor</div>
            <p className="font-serif text-xl text-forge-100 mb-3">A Socratic editor, not an essay machine.</p>
            <p className="text-sm text-forge-100/60 leading-relaxed">Paste a draft and get clarifying questions, evidence checks, counterarguments, revision priorities, and one next task that keeps your voice intact.</p>
          </Link>
        </div>

        <div className="text-[10px] tracking-[0.4em] uppercase text-gold-500 mb-3">Coming Soon</div>
        <h3 className="font-serif text-3xl sm:text-4xl tracking-tight mb-8 max-w-2xl">Future modules at the forge.</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          {[
            { slug: "library-recommendations", name: "Library Recommendations" },
          ].map((m) => (
            <Link
              key={m.slug}
              to={user ? `/coming-soon/${m.slug}` : "/auth"}
              className="block p-6 border border-white/10 hover:border-gold-500/60 transition-all rounded-sm group"
              data-testid={`future-${m.slug}`}
            >
              <div className="text-[10px] tracking-[0.3em] uppercase text-forge-100/40 mb-3">Module</div>
              <div className="font-serif text-xl group-hover:text-gold-400 transition-colors">{m.name}</div>
            </Link>
          ))}
        </div>
        <div className="mt-12 flex flex-col sm:flex-row items-start sm:items-center gap-4 border border-white/10 rounded-sm p-6 bg-forge-800/40">
          <div className="flex-1">
            <div className="text-[10px] tracking-[0.3em] uppercase text-ember-500 mb-1">Founding ThoughtSmith™ Community</div>
            <p className="font-serif text-xl text-forge-100">Help shape the future of intellectual literacy.</p>
            <p className="text-xs text-forge-100/55 mt-1">Educators, students, librarians, parents, professors, and lifelong learners welcome.</p>
          </div>
          <Link
            to="/community"
            className="px-5 py-2.5 bg-ember-500 hover:bg-ember-400 text-forge-900 font-semibold tracking-wide rounded-sm transition-all whitespace-nowrap"
            data-testid="home-community-cta"
          >
            Become a Founding ThoughtSmith™
          </Link>
        </div>
        <div className="mt-3 text-right">
          <Link to="/community-voice" className="text-xs uppercase tracking-[0.25em] text-forge-100/55 hover:text-gold-400 transition-colors" data-testid="home-community-voice-link">
            Read the Community Voice →
          </Link>
        </div>
      </section>

      <footer className="border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 text-xs uppercase tracking-[0.3em] text-forge-100/40 flex flex-wrap justify-between gap-4">
          <span>ThoughtSmith™ · A forge for ideas</span>
          <span>Read. Think. Question. Grow.</span>
        </div>
      </footer>
    </div>
  );
}
